import { useCallback, useEffect } from 'react'
import type { ResponsiveContainerProps } from 'recharts'
import {
    Bar,
    BarChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    TooltipProps,
    XAxis,
    YAxis,
} from 'recharts'
import { AxisDomain } from 'recharts/types/util/types'
import {
    NyuDataPointsWithParsedTime,
    NyuDataProperties,
    NyuGroupProperties,
    NyuLineProperties,
    NyuSeriesProperties,
} from '../../../shared/types'
import useBreakpoint from '../hooks/useBreakpoint'
import { useHighContrastDisplay } from '../hooks/useHighContrastMode'
import screens from '../screens.json'
import { forecastPostfix, clamp, formatDataPointValue } from '../utils/helpers'
import { lineTypes } from '../utils/options'
import {
    defaultReferenceLineColor,
    useReferenceLines,
} from '../utils/referenceLines'
import { AxisLabelAboveChart } from './AxisLabelAboveChart'
import CustomLegend from './Graph/CustomLegend'
import CustomTooltip from './Graph/CustomTooltip'
import {
    hatchColorOf,
    hatchFill,
    HatchPatterns,
    hatchStrokeWidth,
} from './Graph/HatchPattern'
import { BarLabel } from './Graph/HighContrastMode/BarLabel'
import { ShapeBar } from './ShapeBar'

// a `_forecast` series stacks onto its base series, so both share one slot
const stackIdOf = (series: NyuLineProperties) =>
    series.stack ?? series.key.replace(forecastPostfix, '')

function insertGroupHeaders(data: NyuDataPointsWithParsedTime) {
    const result = []
    const headerIndexes = []
    let lastGroup = null

    for (const item of data) {
        if (
            item.barChartDataPointGroup &&
            item.barChartDataPointGroup !== lastGroup
        ) {
            result.push({ name: item.barChartDataPointGroup })
            headerIndexes.push(result.length - 1)
            lastGroup = item.barChartDataPointGroup
        }
        result.push(item)
    }

    return [result, headerIndexes]
}

// Shape-bars are markers painted on top of a regular bar, so they must not take
// part in recharts' stacking, which would offset them by the value below them.
// Every marker gets an axis of its own and reserves the slots of the columns it
// does not annotate, which leaves it on top of the column it belongs to.
const markerSlotDataKey = '__markerSlot__'

type BarColumn = { key: string; barSize?: number }

const overlayAxisId = (bar: NyuLineProperties) => `overlay-${bar.key}`

function getBarColumns(bars: NyuSeriesProperties): BarColumn[] {
    const regularBars = bars.filter((bar) => !bar.barShapeType)
    const columns: BarColumn[] = []

    for (const bar of regularBars.length ? regularBars : bars) {
        const key = stackIdOf(bar)
        if (!columns.some((column) => column.key === key)) {
            columns.push({ key, barSize: bar.barSize })
        }
    }

    return columns
}

function getShapeBarColumnIndex(
    bar: NyuLineProperties,
    seriesProperties: NyuSeriesProperties,
    columns: BarColumn[]
) {
    const declared = columns.findIndex(
        (column) => column.key === stackIdOf(bar)
    )
    if (declared !== -1) {
        return declared
    }

    const precedingBar = seriesProperties
        .slice(0, seriesProperties.indexOf(bar))
        .toReversed()
        .find((candidate) => !candidate.barShapeType)

    return Math.max(
        columns.findIndex(
            (column) => precedingBar && column.key === stackIdOf(precedingBar)
        ),
        0
    )
}

function CustomCategoryTick({
    x,
    y,
    width: widthFromProps,
    height: heightFromProps,
    payload,
    leftMargin,
    headerIndexes,
    fontSize,
    tickFormatter,
}: {
    x: number
    y: number
    width: number
    height: number
    payload: { value: string; index: number }
    leftMargin: number
    headerIndexes: number[]
    fontSize: string
    tickFormatter: (value: string) => string
}) {
    const isHeader = headerIndexes.includes(payload.index)
    const width = leftMargin + widthFromProps - 10

    // recharts passes chart-height/2 as height, resulting in some labels being omitted.
    // limiting to 50px to avoid this.
    const maxCateoryLabelHeight = 50
    const height = Math.min(heightFromProps, maxCateoryLabelHeight)

    return (
        <foreignObject
            x={x - width}
            y={y - height / 2}
            height={width}
            width={width}
            style={{ overflow: 'visible' }}
        >
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'end',
                    justifyContent: 'center',
                    textAlign: 'right',
                    width,
                    height,
                    fontWeight: isHeader ? 'bold' : 'normal',
                    fontSize,
                }}
            >
                {tickFormatter(payload.value)}
            </div>
        </foreignObject>
    )
}

export function CustomBarChart({
    dataPoints,
    dataProperties,
    seriesProperties,
    groups,
    chartHeight,
    chartWidth,
    onResize,
    setChartHeight,
}: {
    dataPoints: NyuDataPointsWithParsedTime
    dataProperties: NyuDataProperties
    seriesProperties: NyuSeriesProperties
    groups: NyuGroupProperties[]
    chartHeight: number
    chartWidth: number
    onResize?: ResponsiveContainerProps['onResize']
    setChartHeight: (height: number) => void
}) {
    const layout = dataProperties.layout || 'horizontal'
    const horizontal = layout === 'horizontal'
    const vertical = layout === 'vertical'

    const breakpointMdOrLarger = useBreakpoint('md')
    const fontSize = breakpointMdOrLarger ? undefined : '12px'

    const highContrastDisplay = useHighContrastDisplay(
        dataProperties,
        seriesProperties
    )

    const renderTooltip = useCallback(
        (props: TooltipProps<number, string>) => (
            <CustomTooltip
                {...props}
                seriesProperties={seriesProperties}
                dataProperties={dataProperties}
            />
        ),
        [seriesProperties, dataProperties]
    )

    const bars = groups.flatMap((group) => group.lines)
    const usedDataKeys = bars.map((bar) => bar.key)

    const referenceLines = useReferenceLines(
        dataProperties,
        seriesProperties,
        dataPoints
    )

    const hatchColors = bars
        .map(hatchColorOf)
        .filter((color): color is string => color !== undefined)

    const columns = getBarColumns(seriesProperties)
    const shapeBars = seriesProperties.filter((bar) => bar.barShapeType)

    const defaultLeftMargin = vertical ? 250 : 0
    const unscaledLeftMargin = dataProperties.leftMargin || defaultLeftMargin
    const scaledLeftMargin = (chartWidth / screens.lg) * unscaledLeftMargin
    const leftMargin = breakpointMdOrLarger
        ? unscaledLeftMargin
        : scaledLeftMargin

    const [dataPointsWithHeaders, headerIndexes] =
        insertGroupHeaders(dataPoints)

    const numBars = Math.max(columns.length, 1)

    const numDataPoints = dataPointsWithHeaders.length
    const barCount = numDataPoints * numBars
    const xAxisHeight = 23
    const additionalHeight = 30
    const verticalHeight =
        barCount * 20 + numDataPoints * 15 + xAxisHeight + additionalHeight
    const minChartHeight = vertical ? verticalHeight : 0
    const chartSpace = horizontal ? chartWidth - leftMargin : chartHeight
    const barCategoryGap = chartSpace / numDataPoints / (4 * numBars)

    useEffect(() => {
        if (vertical) {
            setChartHeight(minChartHeight)
        }
    }, [minChartHeight, setChartHeight])

    const categoryAxisTickFormatter = useCallback(
        (value: string) => {
            const dataPoint = dataPoints.find(
                (dataPoint) => dataPoint.name === value
            )
            return String(dataPoint?.barChartLabel || value)
        },
        [dataPoints]
    )

    const verticalScaleFactor = vertical
        ? clamp(screens.sm / chartWidth, 1, 1.3)
        : 1
    const effectiveChartHeight = Math.max(
        chartHeight,
        minChartHeight * verticalScaleFactor
    )

    return (
        <>
            <AxisLabelAboveChart dataProperties={dataProperties} />
            <ResponsiveContainer
                height={effectiveChartHeight}
                onResize={onResize}
                className="focus-visible:[&_svg]:!shadow-none" // See 'CSS Bleeding' in README.md
            >
                <BarChart
                    layout={layout}
                    accessibilityLayer={true}
                    data={dataPointsWithHeaders}
                    margin={{
                        left: leftMargin,
                        right: 0,
                    }}
                    barCategoryGap={barCategoryGap}
                >
                    <HatchPatterns colors={hatchColors} />

                    <CartesianGrid
                        vertical={vertical}
                        horizontal={horizontal}
                    />

                    {layout === 'vertical' ? (
                        <XAxis
                            type="number"
                            domain={dataProperties.axisDomain as AxisDomain}
                            tickFormatter={(value) =>
                                formatDataPointValue(
                                    value,
                                    dataProperties.format
                                )
                            }
                        />
                    ) : (
                        <YAxis
                            type="number"
                            domain={dataProperties.axisDomain as AxisDomain}
                            tickFormatter={(value) =>
                                formatDataPointValue(
                                    value,
                                    dataProperties.format
                                )
                            }
                        />
                    )}

                    {layout === 'vertical' ? (
                        <>
                            <YAxis
                                yAxisId="main"
                                type="category"
                                dataKey="name"
                                tickLine={false}
                                tick={(props) => (
                                    <CustomCategoryTick
                                        {...props}
                                        leftMargin={leftMargin}
                                        headerIndexes={headerIndexes}
                                        fontSize={fontSize}
                                        tickFormatter={
                                            categoryAxisTickFormatter
                                        }
                                    />
                                )}
                            />
                            {shapeBars.map((bar) => (
                                <YAxis
                                    key={bar.key}
                                    yAxisId={overlayAxisId(bar)}
                                    type="category"
                                    dataKey="name"
                                    tickLine={false}
                                    hide
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            <XAxis
                                xAxisId="main"
                                type="category"
                                dataKey="name"
                                tickLine={false}
                                tickFormatter={categoryAxisTickFormatter}
                            />
                            {shapeBars.map((bar) => (
                                <XAxis
                                    key={bar.key}
                                    xAxisId={overlayAxisId(bar)}
                                    type="category"
                                    dataKey="name"
                                    tickLine={false}
                                    hide
                                />
                            ))}
                        </>
                    )}

                    {breakpointMdOrLarger && (
                        <Tooltip content={renderTooltip} axisId="main" />
                    )}
                    {bars.map((bar) => {
                        const {
                            key,
                            barSize = undefined,
                            ...barPropsRest
                        } = bar
                        const axisIdProp = {
                            [layout === 'vertical' ? 'yAxisId' : 'xAxisId']:
                                bar.barShapeType ? overlayAxisId(bar) : 'main',
                        }

                        const hatchColor = hatchColorOf(bar)

                        const LabelComponent = (props: any) => (
                            <BarLabel
                                {...props}
                                dataKey={bar.key}
                                seriesProperties={seriesProperties}
                                usedDataKeys={usedDataKeys}
                                highContrastMode={
                                    highContrastDisplay.highContrastMode
                                }
                            />
                        )

                        const barElement = (
                            <Bar
                                key={bar.key}
                                dataKey={bar.key}
                                stackId={
                                    bar.barShapeType
                                        ? undefined
                                        : stackIdOf(bar)
                                }
                                fill={
                                    hatchColor
                                        ? hatchFill(hatchColor)
                                        : bar.color?.toLowerCase()
                                }
                                stroke={hatchColor}
                                strokeWidth={
                                    hatchColor ? hatchStrokeWidth : undefined
                                }
                                isAnimationActive={false}
                                unit={dataProperties.format}
                                barSize={barSize}
                                {...axisIdProp}
                                label={
                                    highContrastDisplay.showLetters
                                        ? LabelComponent
                                        : undefined
                                }
                                shape={
                                    bar.barShapeType ? (
                                        <ShapeBar
                                            layout={layout}
                                            key={key}
                                            dataKey={bar.key}
                                            {...barPropsRest}
                                        />
                                    ) : undefined
                                }
                            />
                        )

                        if (!bar.barShapeType) {
                            return barElement
                        }

                        const columnIndex = getShapeBarColumnIndex(
                            bar,
                            seriesProperties,
                            columns
                        )

                        return columns.map((column, index) =>
                            index === columnIndex ? (
                                barElement
                            ) : (
                                <Bar
                                    key={`${bar.key}-${column.key}`}
                                    dataKey={markerSlotDataKey}
                                    barSize={column.barSize}
                                    isAnimationActive={false}
                                    {...axisIdProp}
                                />
                            )
                        )
                    })}
                    {referenceLines.map((referenceLine) => {
                        const axisProps = vertical
                            ? { x: referenceLine.value, yAxisId: 'main' }
                            : { y: referenceLine.value, xAxisId: 'main' }

                        return (
                            <ReferenceLine
                                key={referenceLine.id}
                                {...axisProps}
                                stroke={
                                    referenceLine.color ||
                                    defaultReferenceLineColor
                                }
                                strokeWidth={referenceLine.strokewidth || 2}
                                strokeDasharray={
                                    lineTypes[referenceLine.linetype || 'solid']
                                }
                                ifOverflow="extendDomain"
                            />
                        )
                    })}
                </BarChart>
            </ResponsiveContainer>
            <CustomLegend
                seriesProperties={seriesProperties}
                dataProperties={dataProperties}
                referenceLines={referenceLines}
            />
        </>
    )
}
